// components/Layout.js
import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import Footer from './Footer';

export default function Layout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const toggleBtnRef = useRef(null);

  // Close sidebar on route change
  useEffect(() => { setOpen(false); }, [location]);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Prevent body scroll when sidebar open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <div className="min-h-screen flex flex-col">

      {/* ── Off-canvas sidebar ── */}
      <aside
        id="app-sidebar"
        className={[
          'fixed top-0 right-0 h-full w-80 max-w-[85vw]',
          'bg-white dark:bg-secondary-900',
          'shadow-sidebar z-sidebar overflow-y-auto',
          'transform transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
        aria-hidden={!open}
      >
        {open && <Sidebar isOpen={open} onNavigate={() => setOpen(false)} />}
      </aside>

      {/* ── Backdrop overlay ── */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-overlay animate-fade-in"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex flex-col min-h-screen">

        {/* ── Sticky header ── */}
        <header className="sticky top-0 z-header bg-white dark:bg-secondary-900 shadow-soft border-b border-black/5">
          <div className="max-w-wide mx-auto px-4 sm:px-6">
            <div className="flex items-center min-h-16 py-2 gap-3">

              {/* Right: hamburger */}
              <button
                ref={toggleBtnRef}
                className="p-2 rounded-lg text-secondary-600 dark:text-secondary-300
                           hover:bg-secondary-100 dark:hover:bg-secondary-800
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500
                           transition-colors duration-200"
                onClick={() => setOpen(v => !v)}
                aria-controls="app-sidebar"
                aria-expanded={open}
                aria-label={open ? 'סגור תפריט' : 'פתח תפריט'}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {open ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>

              {/* Center: brand */}
              <Link to="/tractates"
                    className="flex-1 flex flex-col items-center text-center leading-tight no-underline hover:no-underline min-w-0"
                    dir="rtl">
                <span className="font-extrabold text-secondary-900 dark:text-white text-base sm:text-xl">
                  פותח שערים
                </span>
                <span className="text-secondary-500 dark:text-secondary-400 mt-0.5 text-xs sm:text-sm">
                  הבית למורי ומורות הגמרא בחמ"ד
                </span>
              </Link>

              {/* Left: nav links — stacked */}
              <nav className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-6 flex-shrink-0 text-sm font-medium text-secondary-600 dark:text-secondary-300">
                <Link to="/about"
                      className="no-underline hover:text-primary-600 dark:hover:text-primary-400 hover:no-underline transition-colors">
                  אודות
                </Link>
                <Link to="/inspGreet"
                      className="no-underline hover:text-primary-600 dark:hover:text-primary-400 hover:no-underline transition-colors">
                  ברכת המפמ"ר
                </Link>
              </nav>

            </div>
          </div>
        </header>

        {/* ── Page content ── */}
        <main className="flex-1">
          <Outlet />
        </main>

        <Footer />
      </div>
    </div>
  );
}
